import { includes, keyBy } from "lodash";
import { CalendarMetric, CalendarMode, CalendarView } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { useActivityWarnings } from "PFCore/hooks/queries/activity";
import { ProjectItem } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { WorkforceItem } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { useProfilesWarnings } from "PFCore/hooks/queries/profile";
import { useProfilesAvailabilities } from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import { useMemo } from "react";

import { useBookingModuleContext } from "../../../providers/booking_module_context_provider";
import { WorkforceCalendarData } from "../booking_overview_context.types";
import { useBookingsOverviewContext } from "../bookings_overview_context";
import { BookingRowsContextType } from "./booking_rows_context";
import { useRowBars } from "./use_row_bars";
import { useRowBookings } from "./use_row_bookings";

type UseRowsAdditionalData = {
  rowsByCalendarMode: BookingRowsContextType["rowsByCalendarMode"];
  allBookingsModeRowsState: BookingRowsContextType["allBookingsModeRowsState"];
};
type UseRowsAdditionalDataReturn = Pick<
  BookingRowsContextType,
  | "rowsByKey"
  | "bookingsByRowKey"
  | "barsByRowKey"
  | "warningsByActivity"
  | "warningsByProfile"
  | "availabilitiesByProfile"
  | "rowsLoadingByKey"
>;

export const useRowsAdditionalData = ({
  rowsByCalendarMode,
  allBookingsModeRowsState
}: UseRowsAdditionalData): UseRowsAdditionalDataReturn => {
  const { calendarMode } = useBookingsOverviewContext();
  const {
    calendarData: { view, metric, calendarGranularity }
  } = useBookingsOverviewContext<WorkforceCalendarData>();
  const { calendarRange } = useBookingModuleContext();

  const rowsByKey = useMemo(
    () => keyBy(rowsByCalendarMode[calendarMode], "key"),
    [rowsByCalendarMode, calendarMode]
  );

  const { bookingsByRowKey, bookingsLoadingByRowKey } = useRowBookings({
    allBookingsModeRowsState,
    rowsByKey,
    rows: rowsByCalendarMode[calendarMode]
  });

  const { barsByRowKey } = useRowBars({
    bookingsByRowKey,
    rowsByKey,
    rows: rowsByCalendarMode[calendarMode]
  });

  const { warningsByActivity, activityWarningsLoadingByRowKey } = useActivityWarnings(
    calendarMode === CalendarMode.Projects
      ? rowsByCalendarMode[CalendarMode.Projects]
          .filter(({ type }) => type === RowType.Role)
          .map(({ key, original }) => ({ rowKey: key, id: (original as ProjectItem).id }))
      : []
  );

  const { warningsByProfile, profileWarningsLoadingByRowKey } = useProfilesWarnings(
    calendarMode === CalendarMode.Workforce
      ? rowsByCalendarMode[CalendarMode.Workforce]
          .filter(({ type }) => type === RowType.Profile)
          .map(({ key, original }) => ({ rowKey: key, id: (original as WorkforceItem).id }))
      : [],
    calendarRange
  );

  const shouldFetchHeatMapData =
    calendarMode === CalendarMode.Workforce &&
    includes([CalendarMetric.Utilization, CalendarMetric.Availability, CalendarMetric.TotalHours], metric) &&
    !includes([CalendarView.Compressed, CalendarView.BookingsOnly], view);

  const profiles = shouldFetchHeatMapData
    ? rowsByCalendarMode[CalendarMode.Workforce]
        .filter(({ type }) => type === RowType.Profile)
        .map(({ key, original }) => ({ rowKey: key, id: (original as WorkforceItem).id }))
    : [];

  const { availabilitiesByProfile, availabilitiesLoadingByRowKey } = useProfilesAvailabilities({
    profiles,
    calendarRange,
    calendarGranularity,
    enabled: shouldFetchHeatMapData
  });

  const rowsLoadingByKey = useMemo(
    () =>
      rowsByCalendarMode[calendarMode].reduce(
        (result, { key }) => ({
          ...result,
          [key]:
            bookingsLoadingByRowKey[key] ||
            (calendarMode === CalendarMode.Projects && activityWarningsLoadingByRowKey[key]) ||
            (calendarMode === CalendarMode.Workforce &&
              (profileWarningsLoadingByRowKey[key] || availabilitiesLoadingByRowKey[key]))
        }),
        {} as Record<string, boolean>
      ),
    [rowsByCalendarMode, calendarMode, bookingsLoadingByRowKey, activityWarningsLoadingByRowKey]
  );

  return {
    rowsByKey,
    bookingsByRowKey,
    barsByRowKey,
    warningsByActivity,
    warningsByProfile,
    availabilitiesByProfile,
    rowsLoadingByKey
  };
};
